import { type Editor } from '@tiptap/core';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useChatComposerStore } from '../../../../store/useChatComposerStore';
import { type Skill } from '../../skills';
import { resolveSkillWordSuggestion, type SkillWordMatch } from './matchSkillsByTypedWord';
import { skillCommandInsertContent } from './SkillCommandNode';
import { type SkillSuggestionPillRef } from './SkillSuggestionPill';

export type UseSkillWordSuggestionArgs = {
  editor: Editor | null;
  skills: Skill[];
  /** When false, clear match and do not suggest (e.g. `/` or `@` popup open). */
  enabled: boolean;
};

const setSkillWordSuggestionOpen = (open: boolean) => {
  useChatComposerStore.getState().setChatSkillWordSuggestionOpen(open);
};

export const useSkillWordSuggestion = ({ editor, skills, enabled }: UseSkillWordSuggestionArgs) => {
  const [match, setMatch] = useState<SkillWordMatch | null>(null);
  const pillRef = useRef<SkillSuggestionPillRef>(null);
  const skillsRef = useRef(skills);
  skillsRef.current = skills;
  /** After Escape, suppress this phrase until the words change. A trailing space is still that phrase. */
  const dismissedQueryRef = useRef<string | null>(null);

  const clearMatch = useCallback(() => {
    setSkillWordSuggestionOpen(false);
    setMatch(null);
  }, []);

  const dismissSkillWordSuggestion = useCallback(() => {
    setMatch((current) => {
      dismissedQueryRef.current = current?.query ?? null;
      return null;
    });
    setSkillWordSuggestionOpen(false);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: skills is kept in a ref for listeners; include it in deps so identity changes still recompute
  useEffect(() => {
    if (!enabled || !editor) {
      dismissedQueryRef.current = null;
      clearMatch();
      return;
    }

    const recompute = () => {
      const { selection, doc } = editor.state;

      if (!selection.empty) {
        clearMatch();
        return;
      }

      const $from = selection.$from;
      const textBefore = doc.textBetween($from.start(), $from.pos, undefined, '\ufffc');
      const textAfter = doc.textBetween($from.pos, $from.end(), undefined, '\ufffc');

      const next = resolveSkillWordSuggestion({
        cursorPos: $from.pos,
        skills: skillsRef.current,
        textAfterInBlock: textAfter,
        textBeforeInBlock: textBefore,
      });

      if (!next) {
        dismissedQueryRef.current = null;
        clearMatch();
        return;
      }

      // The matched query keeps a trailing space, so Escape must ignore it or the pill returns on the next key.
      if (dismissedQueryRef.current?.trimEnd() === next.query.trimEnd()) {
        clearMatch();
        return;
      }

      dismissedQueryRef.current = null;
      // Sync store before React paint so canvas Escape capture sees the flag while the pill is up.
      setSkillWordSuggestionOpen(true);
      setMatch(next);
    };

    const handleDestroy = () => {
      dismissedQueryRef.current = null;
      clearMatch();
    };

    // Blur must clear the store flag; otherwise canvas Escape is swallowed with no pill dismiss path
    // (editor handleKeyDown only runs while focused). Do not suppress the query — refocus can reshow.
    const handleBlur = () => {
      clearMatch();
    };

    recompute();
    editor.on('update', recompute);
    editor.on('selectionUpdate', recompute);
    editor.on('blur', handleBlur);
    editor.on('destroy', handleDestroy);

    return () => {
      editor.off('update', recompute);
      editor.off('selectionUpdate', recompute);
      editor.off('blur', handleBlur);
      editor.off('destroy', handleDestroy);
      clearMatch();
    };
  }, [clearMatch, editor, enabled, skills]);

  const selectSkillWordSuggestion = useCallback(
    (skill: Skill) => {
      if (!match || !editor) {
        return;
      }

      // Already focused while typing — commands.focus() short-circuits; avoid view.focus() race.
      editor.commands.focus();
      editor
        .chain()
        .deleteRange({ from: match.from, to: match.to })
        .insertContent(skillCommandInsertContent(skill.slashCommand))
        .run();
      useChatComposerStore.getState().addPendingSkillInstructions(skill);
      dismissedQueryRef.current = null;
      clearMatch();
    },
    [clearMatch, editor, match],
  );

  const handleEditorKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!match) {
        return false;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        dismissSkillWordSuggestion();
        return true;
      }

      if (event.key === 'ArrowUp' || event.key === 'ArrowDown' || event.key === 'Enter') {
        return pillRef.current?.onKeyDown(event) ?? false;
      }

      return false;
    },
    [dismissSkillWordSuggestion, match],
  );

  return {
    clearSkillWordSuggestion: clearMatch,
    dismissSkillWordSuggestion,
    handleEditorKeyDown,
    match,
    pillRef,
    selectSkillWordSuggestion,
  };
};
